import { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUserProfile, getUserProfile, handleReferralSignup, markProfileAsReferred, getProfileByClaimToken, transferPlayerProfileOwnership } from '../services/firestoreService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up with email, password, displayName, and optional referralCode/claimToken/signupContext
  const signup = async (email, password, displayName, referralCode = null, claimToken = null, signupContext = null, signupRedirect = null) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Update the user's display name
    if (displayName) {
      await updateProfile(userCredential.user, {
        displayName: displayName
      });
    }

    // Parse coach username from redirect URL (e.g., /coach/johndoe/... → johndoe)
    let signupCoachUsername = null;
    if (signupRedirect) {
      const coachMatch = signupRedirect.match(/\/coach\/([^/]+)/);
      if (coachMatch) signupCoachUsername = coachMatch[1];
    }

    // Create user profile in Firestore with referral info
    await createUserProfile(userCredential.user.uid, {
      email,
      displayName,
      referrerId: referralCode || null,
      referredAt: referralCode ? new Date().toISOString() : null,
      signupContext: signupContext || 'creator',
      signupCoachUsername,
    });

    // Track referral if a referral code was provided
    if (referralCode) {
      try {
        await handleReferralSignup(userCredential.user.uid, referralCode);
      } catch (error) {
        console.error('Error tracking referral:', error);
        // Don't block signup if referral tracking fails
      }
    }

    // Handle claim token — transfer pre-built profile to new user
    let claimedProfileId = null;
    if (claimToken) {
      try {
        const claimedProfile = await getProfileByClaimToken(claimToken);
        if (claimedProfile) {
          await transferPlayerProfileOwnership(claimedProfile.id, userCredential.user.uid);
          claimedProfileId = claimedProfile.id;
        }
      } catch (error) {
        console.error('Error claiming profile:', error);
        // Don't block signup if claim fails
      }
    }

    return { ...userCredential, claimedProfileId };
  };

  // Sign in with email and password
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    // Fetch user profile
    const profile = await getUserProfile(userCredential.user.uid);
    setUserProfile(profile);
    return userCredential;
  };

  // Sign out
  const logout = async () => {
    setUserProfile(null);
    return await signOut(auth);
  };

  // Send password reset email
  const resetPassword = async (email) => {
    return await sendPasswordResetEmail(auth, email);
  };

  // Sign in with Google
  const signInWithGoogle = async (claimToken = null, signupContext = null, signupRedirect = null) => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    // Check for referral code
    const getReferralCode = () => {
      const sessionRef = sessionStorage.getItem('referralCode');
      if (sessionRef) return sessionRef;

      const storedData = localStorage.getItem('referralCode');
      if (storedData) {
        try {
          const { ref, expiresAt } = JSON.parse(storedData);
          if (Date.now() < expiresAt) return ref;
        } catch (e) {
          // Invalid stored data, ignore
        }
      }
      return null;
    };

    const referralCode = getReferralCode();

    // Also check sessionStorage for claim token if not passed directly
    if (!claimToken) {
      claimToken = sessionStorage.getItem('claimToken') || null;
    }

    // Parse coach username from redirect URL
    let signupCoachUsername = null;
    if (signupRedirect) {
      const coachMatch = signupRedirect.match(/\/coach\/([^/]+)/);
      if (coachMatch) signupCoachUsername = coachMatch[1];
    }

    // Check if user profile exists, if not create one
    let profile = await getUserProfile(user.uid);
    if (!profile) {
      await createUserProfile(user.uid, {
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        photoURL: user.photoURL,
        referrerId: referralCode || null,
        referredAt: referralCode ? new Date().toISOString() : null,
        signupContext: signupContext || 'creator',
        signupCoachUsername,
      });
      profile = await getUserProfile(user.uid);

      // Track referral if a referral code was provided
      if (referralCode) {
        try {
          await handleReferralSignup(user.uid, referralCode);
        } catch (error) {
          console.error('Error tracking referral:', error);
          // Don't block signup if referral tracking fails
        }

        // Clear referral code after successful signup
        localStorage.removeItem('referralCode');
        sessionStorage.removeItem('referralCode');
      }
    }

    // Handle claim token — transfer pre-built profile to new user
    let claimedProfileId = null;
    if (claimToken) {
      try {
        const claimedProfile = await getProfileByClaimToken(claimToken);
        if (claimedProfile) {
          await transferPlayerProfileOwnership(claimedProfile.id, user.uid);
          claimedProfileId = claimedProfile.id;
        }
      } catch (error) {
        console.error('Error claiming profile:', error);
      }
      sessionStorage.removeItem('claimToken');
    }

    setUserProfile(profile);
    return { ...userCredential, claimedProfileId };
  };

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    logout,
    resetPassword,
    signInWithGoogle,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 